import axios from "axios"
import i18next from "i18next"
import type { JsonResponse } from "../Objects/Generics/JsonResponse"
import { API_ROUTES } from "../configuration/RoutesApi"
import { ERequestType } from "../Enum/ERequestType"

const getAuthHeaders = (type: string, requestType: ERequestType = ERequestType.Store) => {
  const headers: Record<string, string> = {
    "Content-Type": type,
    keyapp: API_ROUTES.KEY,
  }

  switch (requestType) {
    case ERequestType.Store: {
      const staffToken = localStorage.getItem(API_ROUTES.TOKEN)

      if (staffToken) {
        headers["Authorization"] = `Bearer ${staffToken.replace(/['"]/g, "")}`

        return headers
      }
      break
    }

    case ERequestType.Customer: {
      const customerRaw = localStorage.getItem(API_ROUTES.TOKEN_CUSTOMER)

      if (customerRaw) {
        headers["Authorization"] = `Bearer ${customerRaw.replace(/['"]/g, "")}`

        return headers
      }
      break
    }

    case ERequestType.Courier: {
      const courierRaw = localStorage.getItem(API_ROUTES.TOKEN_COURIER)

      if (courierRaw) {
        headers["Authorization"] = `Bearer ${courierRaw.replace(/['"]/g, "")}`

        return headers
      }
      break
    }

    case ERequestType.Admin: {
      const adminRaw = localStorage.getItem(API_ROUTES.TOKEN_ADMIN)

      if (adminRaw) {
        headers["Authorization"] = `Bearer ${adminRaw.replace(/['"]/g, "")}`

        return headers
      }
      break
    }

    case ERequestType.ServicePointSelfService: {
      const tabletToken = localStorage.getItem(API_ROUTES.TOKEN_SERVICE_POINT_SELF_SERVICE)

      if (tabletToken) {
        headers["Authorization"] = `Bearer ${tabletToken.replace(/['"]/g, "")}`

        return headers
      }
      break
    }

    case ERequestType.Reseller: {
      const resellerRaw = localStorage.getItem(API_ROUTES.TOKEN_RESELLER)

      if (resellerRaw) {
        headers["Authorization"] = `Bearer ${resellerRaw.replace(/['"]/g, "")}`

        return headers
      }
      break
    }
  }

  return headers
}

function handleApiError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const statusCode = error.response?.status

    const isNetworkError = error.code === "ERR_NETWORK" || error.message === "Network Error"

    if (isNetworkError && !error.response) {
      throw new Error(
        i18next.t("general:label_network_unavailable_error", {
          defaultValue: "Sem internet no momento. Verifique sua conexão e tente novamente.",
        }),
      )
    }

    if (statusCode === 500) {
      throw new Error(
        i18next.t("general:label_server_unavailable_error", {
          defaultValue: "O servidor não está respondendo no momento. Tente novamente em instantes.",
        }),
      )
    }
  }

  const apiError = axios.isAxiosError(error) ? error : null

  const errors = apiError?.response?.data?.errors

  if (errors && typeof errors === "object") {
    const errorMessage = Object.values(errors).flat().join(" ")

    throw new Error(errorMessage)
  }

  throw new Error(
    apiError?.response?.data?.message ||
      (error instanceof Error ? error.message : "") ||
      JSON.stringify(apiError?.response?.data) ||
      "Ocorreu um erro desconhecido.",
  )
}

export async function GetRequest<T>(
  endpoint: string,
  requestType: ERequestType = ERequestType.Store,
): Promise<JsonResponse<T>> {
  try {
    const response = await axios.get<JsonResponse<T>>(`${API_ROUTES.URL_API}${endpoint}`, {
      headers: getAuthHeaders("application/json", requestType),
      timeout: 60000,
    })

    return response.data
  } catch (error: unknown) {
    handleApiError(error)
  }
}

export function OpenPdfFromBase64(base64: string, fileName: string, openInBrowser: boolean = false): void {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)

  const byteCharacters = atob(base64)
  const byteNumbers = new Array(byteCharacters.length)

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }

  const byteArray = new Uint8Array(byteNumbers)
  const blob = new Blob([byteArray], { type: "application/pdf" })

  const url = URL.createObjectURL(blob)

  if (isIOS || openInBrowser) {
    window.open(url, "_blank")
    return
  }

  const a = document.createElement("a")
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()

  URL.revokeObjectURL(url)
}

export async function DownloadFileRequest(
  endpoint: string,
  fileName: string,
  requestType: ERequestType = ERequestType.Store,
): Promise<void> {
  try {
    const response = await axios.get(`${API_ROUTES.URL_API}${endpoint}`, {
      headers: getAuthHeaders("application/json", requestType),
      responseType: "blob",
      timeout: 60000,
    })

    if (response.headers["content-type"]?.includes("application/json")) {
      const payload = JSON.parse(await response.data.text()) as Partial<JsonResponse<unknown>>

      if (typeof payload === "object" && payload !== null && "success" in payload && payload.success === false) {
        throw new Error(payload.message)
      }
    }

    const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
    const anchor = document.createElement("a")

    anchor.href = blobUrl
    anchor.setAttribute("download", fileName)
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()

    window.URL.revokeObjectURL(blobUrl)
  } catch (error: unknown) {
    handleApiError(error)
  }
}

export async function GetBlobRequest(
  endpoint: string,
  requestType: ERequestType = ERequestType.Store,
): Promise<Blob> {
  try {
    const response = await axios.get<Blob>(`${API_ROUTES.URL_API}${endpoint}`, {
      headers: getAuthHeaders("application/json", requestType),
      responseType: "blob",
      timeout: 60000,
    })

    if (response.headers["content-type"]?.includes("application/json")) {
      const payload = JSON.parse(await response.data.text()) as Partial<JsonResponse<unknown>>

      if (payload.success === false) {
        throw new Error(payload.message)
      }
    }

    return response.data
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
      const contentType = error.response.data.type || error.response.headers["content-type"]

      if (contentType?.includes("application/json")) {
        let payload: Partial<JsonResponse<unknown>> | undefined

        try {
          payload = JSON.parse(await error.response.data.text()) as Partial<JsonResponse<unknown>>
        } catch {
          payload = undefined
        }

        if (payload?.message) {
          throw new Error(payload.message)
        }
      }
    }

    handleApiError(error)
  }
}

const appendFormData = (formData: FormData, data: Record<string, unknown>, parentKey?: string) => {
  for (const key in data) {
    const value = data[key]
    const formKey = parentKey ? `${parentKey}.${key}` : key

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item instanceof File || item instanceof Blob) {
          formData.append(formKey, item)
        } else if (item !== null && typeof item === "object") {
          appendFormData(formData, item as Record<string, unknown>, `${formKey}[${index}]`)
        } else {
          formData.append(`${formKey}[${index}]`, String(item))
        }
      })
      continue
    }

    if (value instanceof File || value instanceof Blob) {
      formData.append(formKey, value)
    } else if (value !== null && typeof value === "object") {
      appendFormData(formData, value as Record<string, unknown>, formKey)
    } else if (value !== undefined && value !== null) {
      formData.append(formKey, String(value))
    }
  }
}

export async function PostFormRequest<T>(
  endpoint: string,
  body: Record<string, unknown>,
  requestType: ERequestType = ERequestType.Store,
): Promise<JsonResponse<T>> {
  try {
    const formData = new FormData()
    appendFormData(formData, body)

    const response = await axios.post<JsonResponse<T>>(`${API_ROUTES.URL_API}${endpoint}`, formData, {
      headers: getAuthHeaders("multipart/form-data", requestType),
      timeout: 60000,
    })

    return response.data
  } catch (error: unknown) {
    handleApiError(error)
  }
}

export async function PutFormRequest<T>(
  endpoint: string,
  body: Record<string, unknown>,
  requestType: ERequestType = ERequestType.Store,
): Promise<JsonResponse<T>> {
  try {
    const formData = new FormData()
    appendFormData(formData, body)

    const response = await axios.put<JsonResponse<T>>(`${API_ROUTES.URL_API}${endpoint}`, formData, {
      headers: getAuthHeaders("multipart/form-data", requestType),
      timeout: 60000,
    })

    return response.data
  } catch (error: unknown) {
    handleApiError(error)
  }
}

export async function PostRequest<T>(
  endpoint: string,
  body: unknown,
  requestType: ERequestType = ERequestType.Store,
): Promise<JsonResponse<T>> {
  try {
    const response = await axios.post<JsonResponse<T>>(`${API_ROUTES.URL_API}${endpoint}`, body, {
      headers: getAuthHeaders("application/json", requestType),
      timeout: 60000,
    })

    return response.data
  } catch (error: unknown) {
    handleApiError(error)
  }
}

export async function PutRequest<T, TBody = unknown>(
  endpoint: string,
  body: TBody,
  requestType: ERequestType = ERequestType.Store,
): Promise<JsonResponse<T>> {
  try {
    const response = await axios.put<JsonResponse<T>>(`${API_ROUTES.URL_API}${endpoint}`, body, {
      headers: getAuthHeaders("application/json", requestType),
      timeout: 60000,
    })

    return response.data
  } catch (error: unknown) {
    handleApiError(error)
  }
}

export async function DeleteRequest<T>(
  endpoint: string,
  requestType: ERequestType = ERequestType.Store,
): Promise<JsonResponse<T>> {
  try {
    const response = await axios.delete<JsonResponse<T>>(`${API_ROUTES.URL_API}${endpoint}`, {
      headers: getAuthHeaders("application/json", requestType),
      timeout: 60000,
    })

    return response.data
  } catch (error: unknown) {
    handleApiError(error)
  }
}
